import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle, XCircle, Sparkles } from 'lucide-react';
import './Toast.css';

const Toast = ({ 
  message, 
  type = 'info', 
  duration = 4000, 
  onClose,
  position = 'top-right',
  title,
  showProgress = true,
  icon: CustomIcon,
  dismissible = true,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);

  const icons = {
    success: <CheckCircle className="w-5 h-5" />,
    error: <XCircle className="w-5 h-5" />,
    warning: <AlertTriangle className="w-5 h-5" />,
    info: <Info className="w-5 h-5" />,
  };

  const colors = {
    success: { 
      bg: 'bg-emerald-50', 
      border: 'border-emerald-500', 
      text: 'text-emerald-700', 
      icon: 'text-emerald-500',
      progress: 'bg-emerald-500'
    },
    error: { 
      bg: 'bg-red-50', 
      border: 'border-red-500', 
      text: 'text-red-700', 
      icon: 'text-red-500',
      progress: 'bg-red-500'
    },
    warning: { 
      bg: 'bg-amber-50', 
      border: 'border-amber-500', 
      text: 'text-amber-700', 
      icon: 'text-amber-500',
      progress: 'bg-amber-500'
    },
    info: { 
      bg: 'bg-blue-50', 
      border: 'border-blue-500', 
      text: 'text-blue-700', 
      icon: 'text-blue-500',
      progress: 'bg-blue-500'
    },
  };

  const positions = {
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6',
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-center': 'top-6 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-6 left-1/2 -translate-x-1/2',
  };

  useEffect(() => {
    if (!duration) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - (100 / (duration / 50));
        return Math.max(0, newProgress);
      });
    }, 50);

    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  };

  if (!isVisible) return null;

  const color = colors[type] || colors.info;
  const Icon = CustomIcon || icons[type] || icons.info;

  return (
    <div className={`toast-container ${positions[position]} ${isExiting ? 'toast-exit' : 'animate-slide-in'}`}>
      <div className={`toast-content ${color.bg} border-l-4 ${color.border} shadow-2xl rounded-2xl`}>
        {/* Glow effect */}
        <div className={`toast-glow ${color.icon}`}></div>
        
        <div className="toast-body">
          <div className={`toast-icon ${color.icon}`}>
            {Icon}
          </div>
          <div className="toast-message">
            {title && <div className={`toast-title ${color.text}`}>{title}</div>}
            <div className={`toast-text ${color.text}`}>{message}</div>
          </div>
          {dismissible && (
            <button onClick={handleClose} className="toast-close">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {showProgress && (
          <div className="toast-progress">
            <div 
              className={`toast-progress-bar ${color.progress}`} 
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ── Toast Container ──────────────────────────────────────────────────────────

export const ToastContainer = ({ toasts, onRemove }) => {
  return (
    <div className="toast-container-wrapper">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          title={toast.title}
          onClose={() => onRemove(toast.id)}
        />
      ))}
    </div>
  );
};

export default Toast;